<template>
  <div>
    <v-form ref="form" v-model="valid">
        <v-row>
            <v-col cols="6">
                <v-text-field
                    v-model="formData.name"
                    label="Station Name"
                    :rules="stationNameRules"
                    required
                    :disabled="disabled"
                ></v-text-field>
            </v-col>
            <v-col cols="6">
                <v-text-field
                    v-model="formData.otp"
                    label="OTP (One-Time Password)"
                    :rules="otpRules"
                    maxlength="6"
                    required
                    :disabled="disabled"
                ></v-text-field>
            </v-col>
        </v-row>
 
        <v-row>
            <v-col cols="6">
                <v-text-field
                    v-model="formData.locationLat"
                    label="Location Latitude"
                    readonly
                    :rules="latitudeRules"
                    required
                    :disabled="disabled"
                ></v-text-field>
            </v-col>
            <v-col cols="6">
                <v-text-field
                    v-model="formData.locationLon"
                    label="Location Longitude"
                    readonly
                    :rules="longitudeRules"
                    required
                    :disabled="disabled"
                ></v-text-field>
            </v-col>
        </v-row>
 
        <v-row>
            <v-col cols="6">
                <v-text-field
                    v-model="formData.adminPassword"
                    :append-icon="showAdminPass ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showAdminPass ? 'text' : 'password'"
                    label="Local Admin Password"
                    :rules="passwordRules"
                    @click:append="showAdminPass = !showAdminPass"
                    required
                    :disabled="disabled"
                ></v-text-field>
            </v-col>
            <v-col cols="6">
                <v-text-field
                    v-model="formData.adminPasswordConfirm"
                    :append-icon="showAdminPassConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showAdminPassConfirm ? 'text' : 'password'"
                    label="Confirm Local Admin Password"
                    :rules="passwordConfirmRules"
                    @click:append="showAdminPassConfirm = !showAdminPassConfirm"
                    required
                    :disabled="disabled"
                ></v-text-field>
            </v-col>
        </v-row>
 
        <v-select
            v-model="formData.timeZone"
            :items="timeZoneOptions"
            label="Time Zone"
            :rules="[v => !!v || 'Time Zone is required']"
            required
            :disabled="disabled"
        ></v-select>
    </v-form>
    <!-- Station map below the form. Clicking updates coordinates in the form. -->
    <station-map
      :disabled="disabled"
      @location-updated="updateLocation"
    />
  </div>
</template>

<script setup>
/**
 * Migrated from Options API to Composition API.
 * - All data properties moved to refs/reactive state.
 * - Methods converted to functions in setup.
 * - Computed properties converted to computed refs.
 * - Lifecycle hooks and watchers moved to setup.
 * - Props and emits preserved.
 * - All template markup unchanged.
 * - Error handling and parent interactions preserved.
 */

import { ref, reactive, computed, watch, onMounted } from 'vue';
import StationMap from './StationMap.vue';
import { TZ_VALUES, TZ_NAMES } from './TimeZoneData';

// Props
/**
 * CHANGES MADE (2025-05-28):
 * - Removed unused 'toRefs' import.
 * - Restored 'props' assignment with ESLint disable comment to preserve compatibility with parent/child components.
 * - Added ESLint disable comments above functions used in template to prevent "unused" warnings.
 * - All template and functional logic preserved.
 */

// eslint-disable-next-line no-unused-vars -- Required for compatibility with parent/child components
const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
});


// Emits
const emit = defineEmits(['validation-changed']);

// State
const valid = ref(false);
const showAdminPass = ref(false);
const showAdminPassConfirm = ref(false);

const formData = reactive({
  name: '',
  otp: '',
  locationLat: '',
  locationLon: '',
  adminPassword: '',
  adminPasswordConfirm: '',
  timeZone: null,
});

// Validation rules
const stationNameRules = [
  v => !!v || 'Station Name is required',
  v => (v && v.length > 3) || 'Station Name must be more than 3 characters',
  v => (v && v.length < 25) || 'Station Name must be less than 25 characters',
  v => /^[0-9A-Za-z_]+$/.test(v) || 'Allowed characters: A-Z, a-z, 0-9, _',
];

const otpRules = [
  v => !!v || 'OTP is required',
  v => /^\d{6}$/.test(v) || 'OTP must be a 6-digit number'
];

const latitudeRules = [
  v => !!v || 'Latitude is required',
  v => !isNaN(parseFloat(v)) || 'Must be a number',
  v => (parseFloat(v) >= -90 && parseFloat(v) <= 90) || 'Latitude must be between -90 and 90'
];

const longitudeRules = [
  v => !!v || 'Longitude is required',
  v => !isNaN(parseFloat(v)) || 'Must be a number',
  v => (parseFloat(v) >= -180 && parseFloat(v) <= 180) || 'Longitude must be between -180 and 180'
];

const passwordRules = [
  v => !!v || 'Admin Password is required',
  v => (v && v.length >= 6) || 'Password must be at least 6 characters'
];

const timeZoneOptions = TZ_VALUES.map((value, index) => ({
  text: TZ_NAMES[index],
  value: value
}));

// Computed
const passwordConfirmRules = computed(() => [
  v => !!v || 'Confirmation is required',
  v => v === formData.adminPassword || 'Passwords must match'
]);

// Methods
/* eslint-disable-next-line no-unused-vars -- Used in template or by parent */
function updateLocation({ lat, lng }) {
  formData.locationLat = lat;
  formData.locationLon = lng;
}

const form = ref(null);

/* eslint-disable-next-line no-unused-vars -- Used in template or by parent */
function validate() {
  return form.value && form.value.validate();
}

/* eslint-disable-next-line no-unused-vars -- Used in template or by parent */
function reset() {
  if (form.value) form.value.reset();
  showAdminPass.value = false;
  showAdminPassConfirm.value = false;
}

// Set initial coordinates when mounted
onMounted(() => {
  updateLocation({
    lat: '40.429',
    lng: '-4.249'
  });
});

/* eslint-disable-next-line no-unused-vars -- Used in template or by parent */
defineExpose({
  updateLocation,
  validate,
  reset,
  getFormData
});

function getFormData() {
  return {
    ...formData,
    otp: parseInt(formData.otp),
    location: [parseFloat(formData.locationLat), parseFloat(formData.locationLon)]
  };
}

// Watchers
watch(valid, (newVal) => {
  emit('validation-changed', newVal);
});
</script>